import { DealColumn, DealData } from "@/pages/deals/types";
import {
  DATE_BUCKETS,
  DATE_BUCKET_SEPARATOR
} from "@/entities/dataTables/monthGrouping";

export {
  buildGroupSummaries,
  groupFieldValues,
  rekeyGroupSummaries,
  type GroupSummary,
  type GroupSummaryMap
} from "@/components/controls/DataTable/groupSummaries";

/**
 * Date columns the API buckets by month, quarter and year. Only date-only
 * columns: the server buckets a timestamp in UTC while the table buckets
 * created and closed by the viewer's local day, so their totals would land in
 * a neighbouring group.
 */
const SERVER_BUCKETED_DATE_COLUMNS = [DealColumn.EXPECTED_CLOSE_AT];

const dateBucketAggregations = Object.fromEntries(
  SERVER_BUCKETED_DATE_COLUMNS.flatMap((column) =>
    DATE_BUCKETS.map((bucket) => [
      bucket === "month"
        ? column
        : `${column}${DATE_BUCKET_SEPARATOR}${bucket}`,
      { field: column as keyof DealData, function: bucket }
    ])
  )
);

/**
 * How the API groups its aggregations for a group option: the two linked names
 * group on the id they are resolved from, and a date bucket on its date with the
 * server's own month, quarter or year function. A group option with no entry
 * here counts only the rows infinite scroll has reached.
 */
const AGGREGATION_BY_GROUP_FIELD: Record<
  string,
  { field: keyof DealData; function?: string }
> = {
  [DealColumn.STATUS]: { field: DealColumn.STATUS as keyof DealData },
  [DealColumn.STAGE]: { field: DealColumn.STAGE as keyof DealData },
  [DealColumn.MANAGED_BY]: { field: DealColumn.MANAGED_BY as keyof DealData },
  [DealColumn.AE_CONFIDENCE_LEVEL]: {
    field: DealColumn.AE_CONFIDENCE_LEVEL as keyof DealData
  },
  ...dateBucketAggregations,
  [DealColumn.COMPANY_NAME]: { field: "companyId" },
  [DealColumn.PRODUCT_NAME]: { field: "productId" }
};

export const dealGroupAggregation = (groupField?: string) =>
  groupField ? AGGREGATION_BY_GROUP_FIELD[groupField] : undefined;

export const supportsGroupSummaries = (groupField?: string) =>
  !!dealGroupAggregation(groupField);
